/**
 * useBomReportData — load thông tin BOM theo PO (LIFFE/Diff + Input/Output codes)
 * DÙNG CHUNG endpoint với Block Overview của tab BOM ([[BomTab]]): cùng RTK Query
 * (`bom-options` / `batch-report-persist` / `bom-crop-options` / `bom-detail` /
 * `bom-quality-refs`) nên share cache, không gọi trùng.
 *
 * Crop-year resolve y hệt BomTab: crop đã lưu (PO) → crop suggest theo PO date có
 * recipe → crop mới nhất có recipe → currentCropYear.
 *   - LIFFE / Diff(G2): Robusta ref `R13-ST1` từ v_changecost_quality theo PO date.
 *   - Input/Output codes: `bomDetail.inputs/outputs` (định nghĩa BOM của PO) →
 *     dùng cho Overview + phân loại BOM vs Other ([[project-production-report-style]]).
 */
import { useMemo } from 'react'

import {
  useGetBatchReportPersistQuery,
  useGetBomCropOptionsQuery,
  useGetBomQualityRefsQuery,
  useGetFactoryBomDetailQuery,
  useGetFactoryBomOptionsQuery,
} from '../../../../store/api/factoryBomApi'

export interface BomReportData {
  /** LIFFE Robusta (R13-ST1). */
  liffe: number | null
  /** LIFFE Arabica (A13-WA1). */
  liffeAra: number | null
  /** G2 Diffs = premium Robusta (R13-ST1). */
  diff: number | null
  /** Diff Arabica (A13-WA1). */
  diffAra: number | null
  /** Mã Input định nghĩa trong BOM (recipe) — FULL, cho phân band BOM/Other. */
  inputCodes: string[]
  /** Mã Output định nghĩa trong BOM (recipe) — FULL, cho phân band BOM/Other. */
  outputCodes: string[]
  /** Input/Output codes cho Block Overview — chỉ quality Final>0 (recipe ⊕ bom_edit),
   *  KHỚP BomTab Overview (Input quality / Target quality chips theo từng PO). */
  overviewInputCodes: string[]
  overviewOutputCodes: string[]
  /** BOM % (phân số) theo code — input + output recipe (Production Analysis cột E). */
  bomPctByCode: Record<string, number>
  /** Premium vs G2 (USD/MT) theo code — qrefs (Production Analysis cột Premium). */
  premiumByCode: Record<string, number>
  /** Category (Item.Phase) theo code — bomOptions.qualityItems (cột Category). */
  categoryByCode: Record<string, string>
  /** BOM loss (phân số): processing + moisture — Production Analysis K50 = -(2). */
  processingLoss: number | null
  moistureLoss: number | null
  cropYear: string
  isLoading: boolean
  /** Đã có ít nhất 1 nguồn data (detail hoặc ref) → cho phép thay fallback mock. */
  hasData: boolean
}

export function useBomReportData(args: {
  factoryCode?: string
  bomCode?: string
  poNo?: string
  poDateISO?: string
}): BomReportData {
  const factoryCode = args.factoryCode ?? ''
  const bomCode = args.bomCode ?? ''
  const poNo = args.poNo?.trim() ?? ''
  const poDateISO = args.poDateISO || ''

  const { data: bomOptions } = useGetFactoryBomOptionsQuery(factoryCode, { skip: !factoryCode })
  const { data: persist } = useGetBatchReportPersistQuery(
    { factoryCode, poNo },
    { skip: !factoryCode || !poNo },
  )
  const { data: cropOpts } = useGetBomCropOptionsQuery(
    { factoryCode, bomCode, date: poDateISO || undefined },
    { skip: !factoryCode || !bomCode },
  )

  // Crop-year resolve: crop đã lưu → suggest (nếu có recipe) → mới nhất → current.
  const cropYear = useMemo(() => {
    const saved = persist?.bomCrop?.trim()
    if (saved) return saved
    const years = cropOpts?.cropYears ?? []
    const sug = cropOpts?.suggested ?? ''
    if (sug && years.includes(sug)) return sug
    if (years.length) return years[0]
    return sug || bomOptions?.currentCropYear || ''
  }, [persist, cropOpts, bomOptions])

  const { data: bomDetail, isFetching: detailFetching } = useGetFactoryBomDetailQuery(
    { factoryCode, bomCode, cropYear },
    { skip: !factoryCode || !bomCode || !cropYear },
  )
  const { data: qrefs, isFetching: qrefsFetching } = useGetBomQualityRefsQuery(
    { factoryCode, date: poDateISO || undefined },
    { skip: !factoryCode },
  )

  return useMemo(() => {
    // Cùng nguồn ref với BomTab Overview: Robusta R13-ST1, Arabica A13-WA1.
    const robusta = qrefs?.items.find((i) => i.code === 'R13-ST1')
    const arabica = qrefs?.items.find((i) => i.code === 'A13-WA1')

    // bomPctByCode = % RECIPE (baseline, không overlay edit) — cho cột BOM % Analysis.
    const bomPctByCode: Record<string, number> = {}
    for (const c of bomDetail?.inputs ?? []) bomPctByCode[c.quality_code] = c.quality_value ?? 0
    for (const c of bomDetail?.outputs ?? []) bomPctByCode[c.quality_code] = c.quality_value ?? 0
    const premiumByCode: Record<string, number> = {}
    for (const it of qrefs?.items ?? []) if (it.premium != null) premiumByCode[it.code] = it.premium
    const categoryByCode: Record<string, string> = {}
    for (const q of bomOptions?.qualityItems ?? []) {
      const cat = (q as { category?: unknown }).category
      if (typeof cat === 'string') categoryByCode[q.code] = cat
    }

    // Overview chips — KHỚP BomTab Overview: Final % = bom_edit nếu có, else recipe;
    // chỉ giữ code có Final > 0 (quality active của PO). FULL recipe codes giữ riêng
    // cho phân band BOM/Other ở BudgetActualGrid.
    const editItems = persist?.bomEdit?.items ?? {}
    const inVal = new Map<string, number>()
    const outVal = new Map<string, number>()
    for (const c of bomDetail?.inputs ?? []) inVal.set(c.quality_code, c.quality_value ?? 0)
    for (const c of bomDetail?.outputs ?? []) outVal.set(c.quality_code, c.quality_value ?? 0)
    for (const [code, e] of Object.entries(editItems)) {
      if (e?.in != null) inVal.set(code, e.in)
      if (e?.out != null) outVal.set(code, e.out)
    }
    const overviewInputCodes = [...inVal.entries()].filter(([, v]) => (v ?? 0) > 0).map(([c]) => c)
    const overviewOutputCodes = [...outVal.entries()].filter(([, v]) => (v ?? 0) > 0).map(([c]) => c)

    return {
      liffe: robusta?.liffe ?? null,
      liffeAra: arabica?.liffe ?? null,
      diff: robusta?.diff ?? null,
      diffAra: arabica?.diff ?? null,
      inputCodes: bomDetail?.inputs.map((c) => c.quality_code) ?? [],
      outputCodes: bomDetail?.outputs.map((c) => c.quality_code) ?? [],
      overviewInputCodes,
      overviewOutputCodes,
      bomPctByCode,
      premiumByCode,
      categoryByCode,
      processingLoss: bomDetail?.processingLoss ?? null,
      moistureLoss: bomDetail?.moistureLoss ?? null,
      cropYear,
      isLoading: detailFetching || qrefsFetching,
      hasData: !!bomDetail || !!robusta || !!arabica,
    }
  }, [qrefs, bomDetail, bomOptions, persist, cropYear, detailFetching, qrefsFetching])
}
